import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import { push_undo } from 'data/lib/undo'
import { diff_obj } from 'data/lib/diff'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import notificationService from "data/notification"
import { PromiseWatch } from "./lib/promise-helpers"
import validationService from "data/validate"

// private module logic //

export type Chart = {
  chartid: number
  code: string
  name?: string
  isdefault?: boolean
}

const id: string = "chart"
const initialState: ApiResult<Chart[]> = { id, state: "init", value: []}
const chartStream = new BehaviorSubject<ApiResult<Chart[]>>(initialState)
const chartEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let chartMap = {}
const _patches_ = PromiseWatch()

function sortData(data: Chart[]): Chart[] {
  const sorted = data.sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code")));
  const idx = sorted.findIndex(obj => obj['isdefault'] === true);
  sorted.unshift(sorted.splice(idx, 1)[0]);
  return sorted;
}

function clear(): void {
  chartStream.next({ id, state: 'ok', value: [] })
  chartMap = {}
}

function get(chartMapId: number): Chart {
  return chartMap[chartMapId]
}

function fetchCharts(): Promise<Chart[] | { message: string }> {
  clear()
  chartStream.next({ id, state: 'busy', value: chartStream.getValue().value })
  let url = `chart-ui`
  return Client.get(url)
  .then((records: Chart[]) => {
    chartStream.next({ id, state: 'ok', value: sortData(records) })
    validationService.fetchAll('chart')
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function fetch(chartid: number): Promise<Chart | { message: string }> {
  let url = `chart-ui/${chartid}`
  return Client.get(url)
  .then((chart: Chart) => {
    validationService.fetch('chart', chartid)
    return chart
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

export type ChartChild = "summaryaccount" | "detailaccount" | "cashflowmap"
function countChildren(chartid: number, childType: ChartChild): Promise<number | { message: string }> {
  return Client.get(`${childType}?chartid=eq.${chartid}`)
  .then((records: Chart[]) => {
    return records.length
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 3000 })
    return err
  })
}

function create(chart?: Chart): Promise<Chart | { message: string }> {
  const streamVal = chartStream.getValue().value
  chartStream.next({ id, state: 'busy', value: streamVal })

  const payload = chart || {}

  const url = window.location.search.includes('createSampleAccounts') ? 'chart-ui?createSampleAccounts' : 'chart-ui'
  return Client.post(url, payload)
    .then((chart: Chart) => {
      push_undo({
        undofunc: () => deleteOne(chart.chartid),
        redofunc: () => create(chart)
      })
      chartMap[chart.chartid] = chart
      // merge results unique by chartid
      const streamVal = chartStream.getValue().value
      chartStream.next({
        id,
        state: 'ok',
        value: _.unionBy([chart], streamVal, 'chartid')
      })
      chartEventStream.next({ type: 'CHART_CREATED', payload: chart })
      validationService.fetch('chart', chart.chartid)
      return chart
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      chartStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

function copy(chartid:number, code:string, name:string) {
  const streamVal = chartStream.getValue().value;
  chartStream.next({ id, state: 'busy', value: streamVal });

  const payload = {
    table: 'chart',
    recordid: chartid,
    _synchronous: true,
    unique: true
  };

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((charts: Chart[]) => {
      const newRecord = charts[0]
      push_undo({
        undofunc: () => deleteOne(newRecord.chartid),
        redofunc: () => copy(chartid, code, name)
      });
      chartMap[newRecord.chartid] = newRecord
      // merge results unique by chartid
      const streamVal = chartStream.getValue().value
      chartStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], streamVal, 'chartid')
      })
      chartEventStream.next({ type: 'CHART_COPIED', payload: newRecord })
      validationService.fetchAll('chart')

      fetchCharts()
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      chartStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      return err
    })
}

function copies(chartid:number, count: number) {
  const streamVal = chartStream.getValue().value;
  chartStream.next({ id, state: 'busy', value: streamVal });

  const payload = {
    table: 'chart',
    recordid: chartid,
    _synchronous: true,
    unique: true,
    count
  };

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((charts: Chart[]) => {
      push_undo({
        undofunc: () => deleteMultiple(_.map(charts, 'chartid')),
        redofunc: () => copies(chartid, count)
      });
      _.forEach(charts, newRecord => chartMap[newRecord.chartid] = newRecord)
      // merge results unique by chartid
      const streamVal = chartStream.getValue().value
      chartStream.next({
        id,
        state: 'ok',
        value: _.unionBy(charts, streamVal, 'chartid')
      })
      chartEventStream.next({ type: 'CHART_COPIED', payload: charts })
      validationService.fetchAll('chart')

      fetchCharts()
      return charts
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      chartStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      return err
    })
}

async function deleteOne(
  chartid: number,
): Promise<boolean | { message: string }> {
  const streamVal = chartStream.getValue().value

  const deleteFn = () => deleteOne(chartid)
  const refreshFn = () => fetchCharts()
  if (!(await handleReferences('chart', chartid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted chartid
  const value = _(streamVal)
    .filter((p) => p.chartid !== chartid)
    .value()
  value.forEach((p) => (chartMap[p.chartid] = p))

  chartStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(chartid).then(() =>
  Client.del(`chart-ui/${chartid}`)
    .then((chart: Chart) => {
      delete chartMap[chart.chartid]
      push_undo({
        undofunc: () => {
          Client.post('chart-ui', chart)
          chartMap[chart.chartid] = chart
          chartStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteOne(chartid)
      })
      validationService.fetchAll('chart')
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      chartStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchCharts()
      return err
    })
  )
}

async function deleteMultiple(
  chartids: number[],
): Promise<boolean | { message: string }> {
  const streamVal = chartStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(ids)
  const refreshFn = () => fetchCharts()
  if (!(await handleReferences('chart', chartids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted chartid
  const value = _(streamVal)
    .filter((p) => !chartids.includes(p.chartid))
    .value()
  value.forEach((p) => (chartMap[p.chartid] = p))

  chartStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(chartids).then(() =>
    Promise.all(_.map(chartids, c => Client.del(`chart-ui/${c}`)
    .then((charts: {chart: Chart}[]) => {
      charts.forEach(c => delete chartMap[c.chart.chartid])
      validationService.fetchAll('chart')
      push_undo({
        undofunc: () => {
          charts.forEach(chart => {
            Client.post('chart-ui', chart)
            chartMap[chart.chart.chartid] = chart
          })
          chartStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteMultiple(chartids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      chartStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchCharts()
      return err
    })
  ))).then(trues => true)
}
function patch(chart: Chart): Promise<Chart | { message: string }> {
  const streamVal = chartStream.getValue().value
  const chartid: number = chart.chartid
  const streamValchart = _.find(streamVal, { chartid })
  const changeset = diff_obj(chart, streamValchart)
  if (_.isEmpty(changeset)) return new Promise(() => streamVal)
  push_undo({
    undofunc: () => patch(streamValchart),
    redofunc: () => patch(chart)
  })

  const newStreamVal = _.cloneDeep(streamVal)
  newStreamVal.splice(_.findIndex(streamVal, a => a.chartid === chartid), 1, chart)

  // optimistic update
  chartStream.next({
    id,
    state: 'ok',
    value: newStreamVal
  })

  return _patches_.watch(chartid, Client.patch(`chart-ui/${chartid}`, changeset)
    .then((chart: Chart) => {
      chartMap[chart.chartid] = chart
      if('code' in changeset) validationService.fetchAll('chart')
      else validationService.fetch('chart', chartid)
      return chart
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      chartStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchCharts()
      return err
    }))

}

// public module interface //

const chartService = {
  stream: () => chartStream.asObservable(),
  value: () => chartStream.getValue().value,
  eventStream: () => chartEventStream.asObservable(),
  clear,
  get,
  fetch,
  fetchCharts,
  countChildren,
  create,
  copy,
  copies,
  deleteOne,
  deleteMultiple,
  patch
}

export default chartService
